import type { TODO } from '../util/types';
import StudyTeam from './StudyTeam';
import type { NuxtApp } from 'nuxt/app';
import type { apiNuxt } from '../api/apiNuxt';

type ExtendedApi = ReturnType<typeof apiNuxt>['API'] & {
  baseUrl: string;
  version: string;
};

export interface TeamConstructorArgs {
  app: NuxtApp['vueApp'] & {
    $nuxt: NuxtApp & {
      $API: ExtendedApi;
    };
  };
  code: string;
  countryId: number;
  countryName: string;
  principalInvestigatorName: string;
  siteActivationDateTimeMillis: number;
  siteSelectionDateTimeMillis: number;
  siteClosureDateTimeMillis: number;
  studySponsorId: number;
  studyCroId: number;
  studyServiceProviderId: number;
  studyAdHocId: number;
  studySiteId: number;
  studyTeam?: StudyTeam;
}

interface UpdatePayload {
  channelId: number | string;
  teamId: number | string;
  $changes: Record<string, TODO>;
  teamName: string;
  teamType: string;
  complianceTrackingEnabled: boolean;
}

interface UpdateOtherPayload {
  channelId: number | string;
  teamId: number | string;
  teamName: string;
  teamType: string;
  complianceTrackingEnabled: boolean;
}

interface SavePayload {
  channelId: number | string;
  teamType: string;
  overseenByTeamIds: number[];
  teamName: string;
  complianceTrackingEnabled: boolean;
  associatedOrganizationId: number;
}

interface SaveOtherPayload {
  channelId: number | string;
  teamName: string;
  teamType: string;
  complianceTrackingEnabled: boolean;
  associatedOrganizationId: number;
}

class Team {
  app: NuxtApp['vueApp'] & {
    $nuxt: NuxtApp & {
      $API: ReturnType<typeof apiNuxt>['API'];
    };
  };
  code: string;
  countryId: number;
  countryName: string;
  principalInvestigatorName: string;
  siteActivationDateTimeMillis: number;
  siteSelectionDateTimeMillis: number;
  siteClosureDateTimeMillis: number;
  studySponsorId: number;
  studyCroId: number;
  studyServiceProviderId: number;
  studyAdHocId: number;
  studySiteId: number;
  studyTeam?: StudyTeam;

  constructor(teams: Omit<TeamConstructorArgs, 'app'>) {
    const nuxtApp = useNuxtApp();
    this.app = nuxtApp.vueApp as typeof nuxtApp.vueApp & {
      $nuxt: NuxtApp & {
        $API: ReturnType<typeof apiNuxt>['API'];
      };
    };

    this.code = teams.code;
    this.countryId = teams.countryId;
    this.countryName = teams.countryName;
    this.principalInvestigatorName = teams.principalInvestigatorName;
    this.siteActivationDateTimeMillis = teams.siteActivationDateTimeMillis;
    this.siteSelectionDateTimeMillis = teams.siteSelectionDateTimeMillis;
    this.siteClosureDateTimeMillis = teams.siteClosureDateTimeMillis;
    this.studySponsorId = teams.studySponsorId;
    this.studyCroId = teams.studyCroId;
    this.studyServiceProviderId = teams.studyServiceProviderId;
    this.studyAdHocId = teams.studyAdHocId;
    this.studySiteId = teams.studySiteId;
    this.studyTeam = teams.studyTeam && new StudyTeam(teams.studyTeam);
  }

  async update({
    channelId,
    teamId,
    $changes,
    teamName,
    teamType,
    complianceTrackingEnabled,
  }: UpdatePayload): Promise<TODO> {
    const query = Object.assign($changes, {
      name: teamName,
      teamType,
      complianceTrackingEnabled,
    });

    return await this.app.config.globalProperties.$axios.$patch(
      this.app.$nuxt.$API.channels.editTeamInfo(channelId, teamId),
      query
    );
  }

  async updateOther({
    channelId,
    teamId,
    teamName,
    teamType,
    complianceTrackingEnabled,
  }: UpdateOtherPayload): Promise<TODO> {
    return await this.app.config.globalProperties.$axios.$patch(
      this.app.$nuxt.$API.channels.editTeamInfo(channelId, teamId),
      {
        name: teamName,
        teamType,
        complianceTrackingEnabled,
      }
    );
  }

  async save({
    channelId,
    teamType,
    overseenByTeamIds,
    teamName,
    complianceTrackingEnabled,
    associatedOrganizationId,
  }: SavePayload): Promise<TODO> {
    const { code, principalInvestigatorName, countryName } = this;
    return await this.app.config.globalProperties.$axios.$post(
      this.app.$nuxt.$API.channels.createTeam(channelId),
      {
        name: teamName,
        teamType,
        code,
        principalInvestigatorName,
        countryName,
        overseenByTeamIds,
        complianceTrackingEnabled,
        associatedOrganizationId,
      }
    );
  }

  async saveOtherTeamType({
    channelId,
    teamName,
    teamType,
    complianceTrackingEnabled,
    associatedOrganizationId,
  }: SaveOtherPayload): Promise<TODO> {
    return await this.app.config.globalProperties.$axios.$post(
      this.app.$nuxt.$API.channels.createTeam(channelId),
      {
        name: teamName,
        teamType,
        complianceTrackingEnabled,
        associatedOrganizationId,
      }
    );
  }
}

export default Team;
